import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { CheckCircle, XCircle, Clock, Gavel } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== DISPUTES TAB ====================
const DISPUTE_CATEGORY_LV = {
  damaged: 'Prece bojāta',
  not_received: 'Nav saņemts',
  wrong_item: 'Nepareiza prece',
  not_as_described: 'Neatbilst aprakstam',
  other: 'Cits',
};

const DISPUTE_STATUS_META = {
  open:     { lv: 'Atvērts',    en: 'Open',     color: 'bg-amber-100 text-amber-800 border-amber-200',  icon: Clock },
  approved: { lv: 'Apstiprināts', en: 'Approved', color: 'bg-green-100 text-green-800 border-green-200',  icon: CheckCircle },
  rejected: { lv: 'Noraidīts',  en: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200',        icon: XCircle },
};

function DisputesTab({ language }) {
  const isLV = language === 'lv';
  const [disputes, setDisputes] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [activeDispute, setActiveDispute] = useState(null);
  const [overrideAction, setOverrideAction] = useState(null);
  const [overrideNote, setOverrideNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/disputes`),
        axios.get(`${API}/disputes/stats`).catch(() => ({ data: null })),
      ]);
      setDisputes(Array.isArray(d.data) ? d.data : []);
      setStats(s.data);
    } catch (e) {
      console.error(e);
      toast.error(isLV ? 'Neizdevās ielādēt strīdus' : 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitOverride = async () => {
    if (!activeDispute || !overrideAction) return;
    if (overrideAction === 'reject' && (overrideNote || '').trim().length < 10) {
      toast.error(isLV ? 'Noraidījuma pamatojumam jābūt vismaz 10 rakstzīmēm' : 'Rejection note must be at least 10 chars');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/disputes/${activeDispute.id}/admin-override`, {
        action: overrideAction,
        response: overrideNote.trim() || undefined,
      });
      if (overrideAction === 'approve') {
        const amount = (data?.refund?.amount || 0).toFixed(2);
        toast.success(isLV ? `Atmaksa apstiprināta — €${amount}` : `Refund approved — €${amount}`);
      } else {
        toast.success(isLV ? 'Strīds noraidīts' : 'Dispute rejected');
      }
      setActiveDispute(null);
      setOverrideAction(null);
      setOverrideNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isLV ? 'Kļūda' : 'Error'));
    } finally {
      setBusy(false);
    }
  };

  const visible = disputes.filter((d) => {
    if (filter === 'all') return true;
    if (filter === 'escalated') return d.status === 'open' && d.escalated_to_admin;
    return d.status === filter;
  }).sort((a, b) => {
    // Open disputes that are escalated bubble to the top
    const aEsc = (a.status === 'open' && a.escalated_to_admin) ? 1 : 0;
    const bEsc = (b.status === 'open' && b.escalated_to_admin) ? 1 : 0;
    if (aEsc !== bEsc) return bEsc - aEsc;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="space-y-6" data-testid="disputes-tab">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4">
            <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">{isLV ? 'Atvērti' : 'Open'}</p>
            <p className="text-3xl font-outfit font-bold text-amber-600">{stats.open}</p>
          </div>
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4">
            <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">{isLV ? 'Apstiprināti' : 'Approved'}</p>
            <p className="text-3xl font-outfit font-bold text-green-600">{stats.approved}</p>
          </div>
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4">
            <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">{isLV ? 'Noraidīti' : 'Rejected'}</p>
            <p className="text-3xl font-outfit font-bold text-red-600">{stats.rejected}</p>
          </div>
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4">
            <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">
              {isLV ? 'Apst. % (30 d.)' : 'Approve % (30d)'}
            </p>
            <p className="text-3xl font-outfit font-bold text-[#D97736]">
              {stats.approve_rate_30d_pct !== null && stats.approve_rate_30d_pct !== undefined
                ? `${stats.approve_rate_30d_pct}%`
                : '—'}
            </p>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: 'open',      lv: 'Atvērti',     en: 'Open' },
          { k: 'escalated', lv: 'SLA eskalēti', en: 'SLA escalated' },
          { k: 'approved',  lv: 'Apstiprināti', en: 'Approved' },
          { k: 'rejected',  lv: 'Noraidīti',   en: 'Rejected' },
          { k: 'all',       lv: 'Visi',        en: 'All' },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${
              filter === f.k
                ? 'bg-[#121212] text-white border-[#121212]'
                : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
            }`}
            data-testid={`disputes-filter-${f.k}`}
          >
            {isLV ? f.lv : f.en}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl h-24 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E5E4E0]">
          <Gavel className="w-12 h-12 mx-auto mb-3 text-[#5C5C5C] opacity-20" />
          <p className="text-[#5C5C5C]">{isLV ? 'Nav strīdu šajā kategorijā' : 'No disputes in this category'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((d) => {
            const meta = DISPUTE_STATUS_META[d.status] || DISPUTE_STATUS_META.open;
            const Icon = meta.icon;
            const amount = ((d.item_price || 0) * (d.item_quantity || 0)).toFixed(2);
            return (
              <div
                key={d.id}
                className="bg-white border border-[#E5E4E0] rounded-2xl p-4 sm:p-5 hover:border-[#D97736] transition-colors"
                data-testid={`dispute-row-${d.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className={`text-xs font-medium ${meta.color} gap-1.5`}>
                        <Icon className="w-3 h-3" />
                        {isLV ? meta.lv : meta.en}
                      </Badge>
                      <span className="text-xs text-[#5C5C5C]">
                        #{d.order_id.slice(-6).toUpperCase()}
                      </span>
                      {d.admin_override && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          {isLV ? 'Admin lēmums' : 'Admin override'}
                        </Badge>
                      )}
                      {d.escalated_to_admin && d.status === 'open' && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 gap-1">
                          <Clock className="w-3 h-3" />
                          {isLV ? 'SLA eskalēts' : 'SLA escalated'}
                        </Badge>
                      )}
                    </div>
                    <p className="font-medium text-[#121212] text-sm">{d.item_title}</p>
                    <p className="text-xs text-[#5C5C5C] mt-1">
                      {isLV ? 'Pircējs' : 'Buyer'}: <span className="text-[#121212]">{d.buyer_name}</span>
                      {' · '}
                      {isLV ? 'Iemesls' : 'Reason'}: <span className="text-[#121212]">
                        {DISPUTE_CATEGORY_LV[d.category] || d.category}
                      </span>
                      {' · '}
                      <span className="font-semibold text-[#D97736]">€{amount}</span>
                    </p>
                    <p className="text-xs text-[#5C5C5C] mt-2 italic line-clamp-2">{d.reason}</p>
                    {d.response && (
                      <p className="text-xs mt-2 p-2 bg-[#FAFAFA] border-l-2 border-[#D97736] rounded-r">
                        <span className="font-medium">{isLV ? 'Atbilde:' : 'Response:'}</span> {d.response}
                      </p>
                    )}
                    {d.refund_amount !== undefined && d.refund_amount !== null && (
                      <p className="text-xs text-green-700 mt-1">
                        ✓ {isLV ? 'Atmaksāts' : 'Refunded'}: €{(d.refund_amount || 0).toFixed(2)} ({d.refund_method})
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {d.status === 'open' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => { setActiveDispute(d); setOverrideAction('approve'); }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`admin-approve-${d.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          {isLV ? 'Apstiprināt' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setActiveDispute(d); setOverrideAction('reject'); }}
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          data-testid={`admin-reject-${d.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          {isLV ? 'Noraidīt' : 'Reject'}
                        </Button>
                      </>
                    )}
                    {d.status === 'rejected' && !d.admin_override && (
                      <Button
                        size="sm"
                        onClick={() => { setActiveDispute(d); setOverrideAction('approve'); }}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid={`admin-override-${d.id}`}
                      >
                        <Gavel className="w-3.5 h-3.5 mr-1" />
                        {isLV ? 'Atcelt noraidījumu' : 'Override reject'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Override confirmation dialog */}
      <AlertDialog
        open={!!activeDispute}
        onOpenChange={(open) => { if (!open) { setActiveDispute(null); setOverrideAction(null); setOverrideNote(''); } }}
      >
        <AlertDialogContent data-testid="dispute-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {overrideAction === 'approve'
                ? (isLV ? 'Apstiprināt atmaksu (Administrators)' : 'Approve refund (Admin)')
                : (isLV ? 'Noraidīt strīdu (Administrators)' : 'Reject dispute (Admin)')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-[#5C5C5C]">
                <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 text-[#121212]">
                  <p className="font-medium text-sm">{activeDispute?.item_title}</p>
                  <p className="text-xs mt-1">
                    {isLV ? 'Summa' : 'Amount'}:{' '}
                    <span className="font-bold text-[#D97736]">
                      €{((activeDispute?.item_price || 0) * (activeDispute?.item_quantity || 0)).toFixed(2)}
                    </span>
                  </p>
                </div>
                {overrideAction === 'approve' && activeDispute?.status === 'rejected' && (
                  <p className="text-amber-700 text-xs bg-amber-50 border border-amber-200 rounded-md p-2">
                    {isLV
                      ? 'Pārdevējs šo strīdu jau bija noraidījis. Šī darbība atcels viņa lēmumu un piespiedīs atmaksu.'
                      : 'Seller had already rejected this dispute. This will override their decision and force the refund.'}
                  </p>
                )}
                <Textarea
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  rows={3}
                  placeholder={overrideAction === 'reject'
                    ? (isLV ? 'Noraidījuma pamatojums (obligāts, vismaz 10 rakstz.)' : 'Rejection reason (required, min 10 chars)')
                    : (isLV ? 'Piezīme pircējam (neobligāta)' : 'Note to buyer (optional)')}
                  className="text-sm"
                  data-testid="override-note"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {isLV ? 'Atpakaļ' : 'Back'}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={submitOverride}
              className={overrideAction === 'approve'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="override-confirm"
            >
              {overrideAction === 'approve'
                ? (isLV ? 'Apstiprināt un atmaksāt' : 'Approve & refund')
                : (isLV ? 'Noraidīt galīgi' : 'Reject finally')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default DisputesTab;
